import {
  useState, useEffect, useCallback, useRef, useContext,
} from 'react';
import {
  useParams, useNavigate, useSearchParams, Link,
} from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import PuzzleEngineV2 from '../components/puzzle/PuzzleEngineV2';
import PuzzleProgressBar from '../components/puzzle/PuzzleProgressBar';
import PauseOverlay from '../components/puzzle/PauseOverlay';
import ResultsModal from '../components/puzzle/ResultsModal';
import SettingsModal from '../components/SettingsModal';
import { getPuzzle, savePuzzleState } from '../services/puzzleApi';
import { completeRoomPuzzle, getRoomParticipantState, saveRoomParticipantState } from '../services/roomApi';
import { useSettings } from '../context/SettingsContext';
import { AuthContext } from '../context/AuthContext';
import { useGuestId } from '../hooks/useGuestId';
import { formatTime } from '../utils/formatters';

function PlayPuzzle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roomCode = searchParams.get('room'); // Get room code from URL if present

  // Ensure guest ID is generated for non-authenticated users (needed for room play)
  useGuestId();
  const { user } = useContext(AuthContext);

  const {
    soundEnabled,
    showTimer,
    showMoveCount,
    confettiEnabled,
  } = useSettings();
  const [showSettings, setShowSettings] = useState(false);
  const [puzzle, setPuzzle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [moveCount, setMoveCount] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const [percentComplete, setPercentComplete] = useState(0);
  const [containerWidth, setContainerWidth] = useState(null);
  const [roomRank, setRoomRank] = useState(null); // Track rank after room completion
  // Track total participants for share text
  const [roomTotalParticipants, setRoomTotalParticipants] = useState(null);
  const [roomState, setRoomState] = useState(null); // Per-participant state when playing in room
  const [isPaused, setIsPaused] = useState(false); // Pause state for gameplay
  const [showResultsModal, setShowResultsModal] = useState(false); // Results modal visibility
  const saveTimeoutRef = useRef(null);
  const timerRef = useRef(null);
  const statsRef = useRef({ elapsedTime: 0, moveCount: 0 });
  const canvasRef = useRef(null);
  const lastPreviewRef = useRef(null); // Store latest preview for unmount save
  const shareMenuRef = useRef(null);
  const wasAlreadyCompleteRef = useRef(false); // Track if puzzle was complete on load
  const completionBannerRef = useRef(null); // Ref for scrolling to completion banner
  const puzzleHeaderRef = useRef(null); // Ref for scrolling to puzzle on new creation
  const puzzleContainerRef = useRef(null); // Ref for measuring container width
  const puzzleConfigRef = useRef(null); // Store puzzle config to avoid callback recreation

  const shareUrl = `${window.location.origin}/play/${id}`;

  // Copy share link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Native share (mobile)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: puzzle?.title || 'Check out this puzzle!',
          text: 'I made a puzzle for you to solve!',
          url: shareUrl,
        });
        setShowShareMenu(false);
      } catch (err) {
        // User cancelled or share failed
      }
    }
  };

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
        setShowShareMenu(false);
      }
    };

    if (showShareMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShareMenu]);

  // Reset state when navigating to a new puzzle/room (client-side navigation)
  // This ensures fresh state for new puzzles and re-triggers ResizeObserver
  useEffect(() => {
    setContainerWidth(null);
    setShowResultsModal(false);
    setIsPaused(false);
  }, [id, roomCode]);

  // Auto-pause when tab becomes hidden or window loses focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && !isComplete && puzzle) {
        setIsPaused(true);
      }
    };

    // Blur catches macOS desktop/space switches (visibilitychange doesn't fire for those)
    const handleBlur = () => {
      if (!isComplete && puzzle) {
        setIsPaused(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
    };
  }, [isComplete, puzzle]);

  // Measure container width after puzzle loads (for responsive canvas sizing)
  // NOTE: isLoading dependency is critical - the DOM element only exists when !isLoading
  useEffect(() => {
    // Wait until loading is complete AND puzzle is loaded AND ref is available
    if (isLoading || !puzzle || !puzzleContainerRef.current) return undefined;

    const element = puzzleContainerRef.current;
    let rafId = null;
    let retryCount = 0;
    const maxRetries = 10;

    const measureWidth = () => {
      const width = element.offsetWidth;
      if (width > 0) {
        setContainerWidth(width);
        return true;
      }
      return false;
    };

    // Retry with requestAnimationFrame if measurement fails
    const retryMeasure = () => {
      if (!measureWidth() && retryCount < maxRetries) {
        retryCount += 1;
        rafId = requestAnimationFrame(retryMeasure);
      }
    };

    // Use ResizeObserver for reliable measurement - fires when element has dimensions
    const resizeObserver = new ResizeObserver((entries) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (width > 0) {
          setContainerWidth(width);
        }
      }
    });

    resizeObserver.observe(element);

    // Try immediate measurement, then retry if needed
    if (!measureWidth()) {
      rafId = requestAnimationFrame(retryMeasure);
    }

    return () => {
      resizeObserver.disconnect();
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [puzzle, isLoading]);

  useEffect(() => {
    const fetchPuzzle = async () => {
      try {
        const data = await getPuzzle(id);
        setPuzzle(data);
        puzzleConfigRef.current = data.config; // Store config in ref for stable callbacks

        // If playing in a room context, load per-participant state instead
        let stateToUse = data.state;
        let statsToUse = data.stats;

        if (roomCode) {
          try {
            const participantData = await getRoomParticipantState(roomCode);
            if (participantData.gameState && participantData.gameState.pieces?.length > 0) {
              // Use participant's saved state
              stateToUse = participantData.gameState;
              setRoomState(participantData.gameState);
            } else {
              // No saved state for this participant - they get a fresh puzzle
              stateToUse = { pieces: [], isComplete: false };
              setRoomState(null);
            }
            // Use participant's stats from room
            if (participantData.stats) {
              statsToUse = {
                totalPlayTime: participantData.stats.playTime || 0,
                moveCount: participantData.stats.moveCount || 0,
              };
            }
          } catch (roomErr) {
            // eslint-disable-next-line no-console
            console.error('Failed to load room state:', roomErr);
            // Fall back to fresh puzzle
            stateToUse = { pieces: [], isComplete: false };
            setRoomState(null);
          }
        }

        const wasComplete = stateToUse?.isComplete || false;
        setIsComplete(wasComplete);
        wasAlreadyCompleteRef.current = wasComplete; // Track initial state for PuzzleCanvas prop

        // Scroll to puzzle header for new puzzles (no saved state = just created)
        // Delay slightly to ensure DOM is rendered
        if (!stateToUse?.pieces || stateToUse.pieces.length === 0) {
          setTimeout(() => {
            puzzleHeaderRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }, 100);
        }

        // Always restore saved stats (for resume or completed puzzles)
        if (statsToUse) {
          const savedTime = statsToUse.totalPlayTime || 0;
          const savedMoves = statsToUse.moveCount || 0;
          setElapsedTime(savedTime);
          setMoveCount(savedMoves);
          statsRef.current = { elapsedTime: savedTime, moveCount: savedMoves };
        }

        // Calculate initial percentage from saved state
        if (stateToUse?.pieces && stateToUse.pieces.length > 0) {
          let connections = 0;
          stateToUse.pieces.forEach((piece) => {
            if (piece.rightConnectionId) connections += 1;
            if (piece.downConnectionId) connections += 1;
          });
          const { rows, columns } = data.config;
          const maxConnections = (rows * (columns - 1)) + ((rows - 1) * columns);
          if (maxConnections > 0) {
            setPercentComplete(Math.round((connections / maxConnections) * 100));
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPuzzle();
  }, [id, roomCode]);

  // Timer effect - start when puzzle loads, stop on completion or pause
  // Also periodically save stats + preview every 30 seconds
  useEffect(() => {
    if (puzzle && !isComplete && !isPaused) {
      timerRef.current = setInterval(() => {
        setElapsedTime((prev) => {
          const newTime = prev + 1;
          statsRef.current.elapsedTime = newTime;

          // Every 30 seconds, save stats and capture a fresh preview
          if (newTime > 0 && newTime % 30 === 0) {
            const freshPreview = canvasRef.current?.getSnapshot?.();
            if (freshPreview) {
              lastPreviewRef.current = freshPreview;
            }

            if (roomCode) {
              // Save to room participant state
              saveRoomParticipantState(roomCode, {
                playTime: newTime,
                moveCount: statsRef.current.moveCount,
                previewImage: freshPreview || lastPreviewRef.current,
              }).catch(() => {
                // Silently fail
              });
            } else {
              // Save to global puzzle state
              savePuzzleState(id, {
                playTime: newTime,
                moves: statsRef.current.moveCount,
                previewImage: freshPreview || lastPreviewRef.current,
              }).catch(() => {
                // Silently fail
              });
            }
          }

          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [puzzle, isComplete, isPaused, id, roomCode]);

  // Calculate percentage complete from piece positions
  const calculatePercentComplete = useCallback((piecePositions, puzzleRows, puzzleCols) => {
    if (!piecePositions || piecePositions.length === 0) return 0;
    let connections = 0;
    piecePositions.forEach((piece) => {
      if (piece.rightConnectionId) connections += 1;
      if (piece.downConnectionId) connections += 1;
    });
    const maxConnections = (puzzleRows * (puzzleCols - 1)) + ((puzzleRows - 1) * puzzleCols);
    if (maxConnections === 0) return 0;
    return Math.round((connections / maxConnections) * 100);
  }, []);

  // Handle move (connection) events
  const handleMove = useCallback(() => {
    setMoveCount((prev) => {
      const newCount = prev + 1;
      statsRef.current.moveCount = newCount;
      return newCount;
    });
  }, []);

  const handleProgress = useCallback((progressData) => {
    // Store latest preview for use on unmount (before canvas is destroyed)
    if (progressData.previewImage) {
      lastPreviewRef.current = progressData.previewImage;
    }

    // Calculate and update percentage complete
    // Use puzzleConfigRef instead of puzzle state to avoid callback recreation
    if (progressData.piecePositions?.pieces && puzzleConfigRef.current) {
      const pct = calculatePercentComplete(
        progressData.piecePositions.pieces,
        puzzleConfigRef.current.rows,
        puzzleConfigRef.current.columns,
      );
      setPercentComplete(pct);
    }

    // Debounce save to avoid too many requests
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        // piecePositions now includes { deviceTier, pieces }
        const { deviceTier, pieces } = progressData.piecePositions;

        if (roomCode) {
          // Save to per-participant room state
          await saveRoomParticipantState(roomCode, {
            pieces,
            deviceTier,
            isComplete: progressData.isComplete,
            previewImage: progressData.previewImage,
            playTime: statsRef.current.elapsedTime,
            moveCount: statsRef.current.moveCount,
          });
        } else {
          // Save to global puzzle state (solo play)
          await savePuzzleState(id, {
            isComplete: progressData.isComplete,
            previewImage: progressData.previewImage,
            piecePositions: pieces,
            deviceTier,
            playTime: statsRef.current.elapsedTime,
            moves: statsRef.current.moveCount,
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to save progress:', err);
      }
    }, 1000);
  }, [id, roomCode, calculatePercentComplete]);

  const handleComplete = useCallback(async (previewImage) => {
    setIsComplete(true);
    setShowResultsModal(true);

    // Stop the timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Fire confetti celebration if enabled
    if (confettiEnabled) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });

      // Second burst from sides for extra celebration
      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
        });
        confetti({
          particleCount: 50,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
        });
      }, 250);
    }

    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Save completion immediately with stats (read from ref to avoid stale closure)
    try {
      await savePuzzleState(id, {
        isComplete: true,
        playTime: statsRef.current.elapsedTime,
        moves: statsRef.current.moveCount,
        previewImage,
      });

      // If playing in a room context, record completion to the room
      if (roomCode) {
        try {
          const result = await completeRoomPuzzle(roomCode, {
            playTime: statsRef.current.elapsedTime,
            moveCount: statsRef.current.moveCount,
          });
          setRoomRank(result.rank);
          setRoomTotalParticipants(result.totalParticipants);
        } catch (roomErr) {
          // eslint-disable-next-line no-console
          console.error('Failed to record room completion:', roomErr);
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to save completion:', err);
    }
  }, [id, confettiEnabled, roomCode]);

  // Store roomCode in a ref so it's accessible in unmount cleanup
  const roomCodeRef = useRef(roomCode);
  roomCodeRef.current = roomCode;

  // Save stats, thumbnail, and cleanup on unmount (when user leaves the page)
  useEffect(() => () => {
    // Clear any pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Save current stats and thumbnail immediately when leaving
    // (if puzzle was loaded and not complete). Use statsRef to get latest values
    if (id && !isComplete && statsRef.current.elapsedTime > 0) {
      // Use cached preview (more reliable) or try to capture from canvas
      const previewImage = lastPreviewRef.current
        || canvasRef.current?.getSnapshot?.()
        || null;

      // Fire and forget - we're unmounting so can't wait for response
      if (roomCodeRef.current) {
        // Save to room participant state
        saveRoomParticipantState(roomCodeRef.current, {
          playTime: statsRef.current.elapsedTime,
          moveCount: statsRef.current.moveCount,
          previewImage,
        }).catch(() => {
          // Silently fail - user is leaving anyway
        });
      } else {
        // Save to global puzzle state
        savePuzzleState(id, {
          playTime: statsRef.current.elapsedTime,
          moves: statsRef.current.moveCount,
          previewImage,
        }).catch(() => {
          // Silently fail - user is leaving anyway
        });
      }
    }
  }, [id, isComplete]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-red-800 dark:text-red-400 mb-2">Puzzle Not Found</h2>
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/create')}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Create New Puzzle
          </button>
        </div>
      </div>
    );
  }

  const { rows, columns } = puzzle.config;
  const pieceCount = rows * columns;

  return (
    <div ref={puzzleContainerRef} className="w-full max-w-7xl mx-auto px-2 sm:px-4 pt-16 sm:pt-20 pb-4 sm:pb-8">
      {/* Results Modal */}
      {showResultsModal && (
        <ResultsModal
          elapsedTime={elapsedTime}
          moveCount={moveCount}
          pieceCount={pieceCount}
          puzzleNumber={0}
          puzzleTitle={puzzle.title}
          onClose={() => setShowResultsModal(false)}
          roomCode={roomCode}
          rank={roomRank}
          totalParticipants={roomTotalParticipants}
          isGuest={!user}
        />
      )}

      {/* Floating Stats Bar - always visible during active play */}
      {!isPaused && !isComplete && (
        <div
          className="fixed top-28 left-1/2 -translate-x-1/2 z-40
                     bg-puzzle-surface/95 backdrop-blur-md
                     rounded-full shadow-lg border border-puzzle-border
                     px-4 py-2 flex items-center gap-3 sm:gap-4"
        >
          {/* Puzzle Info */}
          <span className="font-bold text-puzzle-text text-sm">
            {`${rows}x${columns}`}
          </span>

          {/* Divider */}
          <div className="w-px h-5 bg-puzzle-border" />

          {/* Timer */}
          {showTimer && (
            <span className="flex items-center gap-1 text-sm text-puzzle-text-secondary">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium text-puzzle-text">{formatTime(elapsedTime)}</span>
            </span>
          )}

          {/* Moves */}
          {showMoveCount && (
            <span className="flex items-center gap-1 text-sm text-puzzle-text-secondary">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
              </svg>
              <span className="font-medium text-puzzle-text">{moveCount}</span>
            </span>
          )}

          {/* Progress */}
          <span className="flex items-center gap-1 sm:gap-2 text-sm">
            <div className="w-12 sm:w-24">
              <PuzzleProgressBar progress={percentComplete} color="#2196F3" />
            </div>
            <span className="font-medium text-puzzle-text">
              {percentComplete}
              %
            </span>
          </span>

          {/* Divider */}
          <div className="w-px h-5 bg-puzzle-border" />

          {/* Action buttons */}
          <div className="flex items-center gap-1">
            {/* Settings */}
            <button
              type="button"
              onClick={() => setShowSettings(true)}
              className="p-1.5 text-puzzle-text-secondary hover:text-puzzle-text hover:bg-puzzle-hover rounded-full transition-colors"
              title="Settings"
              aria-label="Open settings"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>

            {/* Shuffle */}
            <button
              type="button"
              onClick={() => canvasRef.current?.shuffle()}
              className="p-1.5 text-puzzle-text-secondary hover:text-puzzle-text hover:bg-puzzle-hover rounded-full transition-colors"
              title="Shuffle pieces"
              aria-label="Shuffle pieces"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>

            {/* Pause */}
            <button
              type="button"
              onClick={() => setIsPaused(true)}
              className="p-1.5 text-puzzle-text-secondary hover:text-puzzle-text hover:bg-puzzle-hover rounded-full transition-colors"
              title="Pause puzzle"
              aria-label="Pause puzzle"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div
        ref={puzzleHeaderRef}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-4 gap-2"
      >
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-puzzle-text">{puzzle.title}</h1>
          <p className="text-sm text-puzzle-text-secondary">
            {`${rows} x ${columns} (${pieceCount} pieces)`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Share Button with Dropdown */}
          <div className="relative" ref={shareMenuRef}>
            <button
              type="button"
              onClick={() => setShowShareMenu(!showShareMenu)}
              className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm sm:text-base"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Share
            </button>

            {/* Dropdown Menu */}
            {showShareMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-puzzle-surface rounded-lg shadow-lg border border-puzzle-border py-1 z-50">
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-puzzle-text hover:bg-puzzle-hover"
                >
                  {copied ? (
                    <>
                      <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-green-600">Copied!</span>
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                      Copy link
                    </>
                  )}
                </button>

                {navigator.share && (
                  <button
                    type="button"
                    onClick={handleNativeShare}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-puzzle-text hover:bg-puzzle-hover"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Share via...
                  </button>
                )}
              </div>
            )}
          </div>

          <Link
            to="/create"
            className="px-3 sm:px-4 py-2 bg-puzzle-muted text-puzzle-text rounded hover:bg-puzzle-hover transition-colors text-sm sm:text-base"
          >
            New Puzzle
          </Link>
        </div>
      </div>

      {/* Completion Banner */}
      {isComplete && (
        <div
          ref={completionBannerRef}
          className="mb-6 bg-green-50 border border-puzzle-green/30 rounded-lg p-4"
        >
          <div className="flex items-center justify-center gap-2">
            <span className="text-lg font-semibold text-puzzle-green">
              Puzzle Complete!
            </span>
          </div>
          <p className="text-center text-puzzle-green/80 mt-2">
            {`Time: ${formatTime(elapsedTime)} | Moves: ${moveCount}`}
          </p>
          <div className="flex justify-center mt-3">
            <button
              type="button"
              onClick={() => setShowResultsModal(true)}
              className="px-4 py-2 bg-puzzle-green text-white text-sm font-medium rounded-full hover:bg-green-600 transition-colors"
            >
              Share Results
            </button>
          </div>
        </div>
      )}

      {/* Puzzle Canvas with Pause Overlay */}
      <div className="relative">
        {containerWidth && (
          <PuzzleEngineV2
            ref={canvasRef}
            imageData={puzzle.imageData}
            rows={puzzle.config.rows}
            columns={puzzle.config.columns}
            pieceStyle={puzzle.config.pieceStyle}
            isAlreadyComplete={wasAlreadyCompleteRef.current}
            savedPiecePositions={roomCode ? roomState?.pieces : puzzle.state?.pieces}
            savedDeviceTier={roomCode ? roomState?.deviceTier : puzzle.state?.deviceTier}
            savedCanvasWidth={roomCode ? roomState?.canvasWidth : puzzle.state?.canvasWidth}
            containerWidth={containerWidth}
            soundEnabled={soundEnabled}
            onComplete={handleComplete}
            onProgress={handleProgress}
            onMove={handleMove}
          />
        )}

        {/* Pause Overlay */}
        <AnimatePresence>
          {isPaused && !isComplete && (
            <PauseOverlay onResume={() => setIsPaused(false)} />
          )}
        </AnimatePresence>
      </div>

      {/* Guest Banner */}
      {!localStorage.getItem('token') && (
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-center">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            {'Playing as guest. '}
            <Link to="/login" className="font-semibold underline hover:text-blue-600 dark:hover:text-blue-400">
              Login
            </Link>
            {' or '}
            <Link to="/register" className="font-semibold underline hover:text-blue-600 dark:hover:text-blue-400">
              Register
            </Link>
            {' to save your puzzles and track stats.'}
          </p>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <SettingsModal onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}

export default PlayPuzzle;
